<template>
    <div>
        <v-overlay :model-value="loading"
                   class="align-center justify-center">
            <v-progress-circular color="primary"
                                 indeterminate
                                 size="64"></v-progress-circular>

        </v-overlay>
        <div v-if="data" class="pa-2">
            <h2>{{ data.year }}</h2>
            <v-row>
                <v-col cols="3" v-for="group in data.contactGroups" :key="group.groupName">
                    <v-card :color="groupDetails(group.groupName).colour" variant="flat" class="text-center" @click="scrollToGroup(group.groupName)">
                        <v-card-text>
                            <h2 class="mb-1 pb-1 border-b-sm"><v-icon start :icon="groupDetails(group.groupName).icon"></v-icon> {{ group.groupName }}</h2>
                            <div class="header">BUDGET</div>
                            <h2>${{ numberWithCommas(groupTotal(group, 'budget'), 0) }} <v-chip class="perc-chip">{{ numberWithCommas((groupTotal(group, 'budget')/allGroupBudget)*100, 0) }}%</v-chip></h2>
                            <div class="header">ESTIMATE</div>
                            <h2>${{ numberWithCommas(groupTotal(group, 'estimatedActual'), 0) }} <v-chip class="perc-chip">{{ numberWithCommas((groupTotal(group, 'estimatedActual')/allGroupEstimate)*100, 0) }}%</v-chip></h2>
                            <v-chip size="small" :class="diffClass(groupPercentDiff(group), 0)" class="ml-1 mt-1"><v-icon :icon="groupPercentDiff(group) > 0 ? 'mdi-arrow-up-bold' : groupPercentDiff(group) == 0 ? 'mdi-minus' : 'mdi-arrow-down-bold'" class="mr-1"></v-icon>{{numberWithCommas(groupPercentDiff(group), 0) + '%'}}</v-chip>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="3">
                    <v-card color="blue-grey-darken-1" variant="flat" class="text-center">
                        <v-card-text>
                            <h2 class="mb-1 pb-1 border-b-sm"><v-icon start icon="mdi-equal"></v-icon> Total</h2>
                            <div class="header">BUDGET</div>
                            <h2>${{ numberWithCommas(allGroupBudget, 0) }} <v-chip class="perc-chip">100%</v-chip></h2>
                            <div class="header">ESTIMATE</div>
                            <h2>${{ numberWithCommas(allGroupEstimate, 0) }} <v-chip class="perc-chip">100%</v-chip></h2>
                            <v-chip size="small" :class="diffClass(allGroupDiff, 0)" class="ml-1 mt-1"><v-icon :icon="allGroupDiff > 0 ? 'mdi-arrow-up-bold' : allGroupDiff == 0 ? 'mdi-minus' : 'mdi-arrow-down-bold'" class="mr-1"></v-icon>{{numberWithCommas(allGroupDiff, 0) + '%'}}</v-chip>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
            <div v-for="group in data.contactGroups" :key="group.groupName" class="mb-3 pb-3" :ref="group.groupName">
                <v-table density="compact" class="my-2 py-2 border-t-md border-b-md" :class="[ 'bg-'+groupDetails(group.groupName).colour ]">
                    <tbody>
                        <tr>
                            <td style="width:16%">
                                <v-chip variant="flat" :color="groupDetails(group.groupName).colour" size="large">
                                    <v-icon start :icon="groupDetails(group.groupName).icon"></v-icon> {{ group.groupName }}
                                </v-chip>
                            </td>
                            <td v-for="month in monthsArray" :key="month.name" class="text-right" style="width:12%">
                                <small>{{ month.name }} {{ data.year }}</small>
                                <h4>{{ numberWithCommas(monthTotal(group, month.index), 0) }}</h4>
                            </td>
                            <td class="text-right" style="width:12%">
                                <small>{{ data.year - 1 }} YTD</small>
                                <h4>{{ numberWithCommas(groupTotal(group, 'lastYTDActual'), 0) }}</h4>
                            </td>
                            <td class="text-right" style="width:12%">
                                <small>{{ data.year }} YTD</small>
                                <h4>{{ numberWithCommas(groupTotal(group, 'ytdActual'), 0) }}</h4>
                            </td>
                            <td class="text-right" style="width:12%">
                                <small>{{ data.year - 1 }} TOTAL</small>
                                <h4>{{ numberWithCommas(lastYearGroupTotal(group), 0) }}</h4>
                            </td>
                            <td class="text-right" style="width:12%">
                                <small>{{ data.year }} EST.</small>
                                <h4>{{ numberWithCommas(groupTotal(group, 'estimatedActual'), 0) }}</h4>
                            </td>
                            <td class="text-right" style="width:12%">
                                <small>BUDGET</small>
                                <h4>{{ numberWithCommas(groupTotal(group, 'budget'), 0) }}</h4>
                            </td>
                            <td class="text-right" style="width:12%">
                                <small>%</small>
                                <h4>{{ groupTotal(group, 'budget') ? numberWithCommas(groupPercentDiff(group), 0) + '%' : '0' }}</h4>
                            </td>
                        </tr>
                    </tbody>
                </v-table>
                <v-table density="compact" class="detail-table">
                    <thead>
                        <tr>
                            <th class="text-left" style="width:16%">
                                Name
                            </th>
                            <th v-for="month in monthsArray" :key="month.name" class="text-right" style="width:12%">
                                {{ month.name }} {{ data.year }}
                            </th>
                            <th class="text-right" style="width:12%">
                                {{ data.year - 1 }} YTD
                            </th>
                            <th class="text-right" style="width:12%">
                                {{ data.year }} YTD
                            </th>
                            <th class="text-right" style="width:12%">
                                {{ data.year - 1 }} Total
                            </th>
                            <th class="text-right" style="width:12%">
                                {{ data.year }} Est.
                            </th>
                            <th class="text-right" style="width:12%">
                                Budget {{ data.year }}
                            </th>
                            <th class="text-right" style="width:12%">%</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in group.contacts" :key="item.contact.id">
                            <td><a href="#" @click="openDetails(item)">{{ item.contact.name }}</a><div class="d-block"><v-chip size="x-small" class="pa-1 d-inline" v-if="item.activeBudget.profabilityRating && item.activeBudget.effortRating" :class="ratingClass(item.activeBudget.profabilityRating, item.activeBudget.effortRating)">Rating: {{ item.activeBudget.profabilityRating - item.activeBudget.effortRating }}</v-chip><small class="text-muted ml-2" v-if="item.activeBudget.notes">{{ item.activeBudget.notes }}</small></div></td>
                            <td v-for="month in monthsArray" :key="month.name" class="text-right">
                                {{ numberWithCommas(item.activeBudget.monthlyTotals[month.index-1], 0) }}
                            </td>
                            <td class="text-right">{{ numberWithCommas(item.activeBudget.lastYTDActual, 0) }}</td>
                            <td class="text-right">{{ numberWithCommas(item.activeBudget.ytdActual, 0) }} <v-chip size="x-small" class="pa-1 d-inline" v-if="item.activeBudget.lastYTDActual != 0" :class="item.activeBudget.ytdActual/item.activeBudget.lastYTDActual > 1 ? 'text-green-darken-1' : 'text-red-darken-1'">{{ numberWithCommas(((item.activeBudget.ytdActual/item.activeBudget.lastYTDActual)-1)*100, 0) }}%</v-chip> </td>
                            <td class="text-right">{{ numberWithCommas(lastYearTotal(item.activeBudget), 0) }}</td>
                            <td class="text-right">{{ numberWithCommas(item.activeBudget.estimatedActual, 0) }}</td>
                            <td class="text-right"><span v-if="item.activeBudget.budget">{{ numberWithCommas(item.activeBudget.budget, 0) }}</span></td>
                            <td class="text-right" :class="diffClass(item.activeBudget.percentageDifference, item.activeBudget.ytdActual)"><span v-if="item.activeBudget.percentageDifference">{{ numberWithCommas(item.activeBudget.percentageDifference, 0) }}%</span></td>
                        </tr>
                    </tbody>
                </v-table>
            </div>
            <v-dialog v-model="showDetails"
                      persistent
                      :scrim="false">
                <v-card v-if="selectedDetails">
                    <v-toolbar dark
                               color="primary">
                        <v-toolbar-title><v-icon icon="mdi-square-edit-outline"></v-icon> Edit {{ selectedDetails.contact.name }} Budget {{ data.year }}</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-btn icon
                               dark
                               @click="showDetails = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-toolbar>
                    <v-card-text>
                        <v-row>
                            <v-col cols="8">
                                <contact-performance-panel :contact="selectedDetails.contact" :hide-panels="true"></contact-performance-panel>
                                <v-row>
                                    <v-col cols="6">
                                        <v-select label="Group *"
                                                  v-model="editGroup"
                                                  :return-object="true"
                                                  :loading="contactGroupsLoading"
                                                  :items="contactGroups"
                                                  item-title="name"
                                                  item-value="id"></v-select>
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field :label="data.year+' Budget'"
                                                      :disabled="detailSaving"
                                                      v-model="editBudget"
                                                      :hint="'Enter the customers total budget figure for calendar '+data.year"
                                                      persistent-hint></v-text-field>
                                        <div class="mb-3">
                                            Budget per month <strong v-if="editBudget && editBudget > 0">${{ numberWithCommas(editBudget/12, 0) }}</strong>
                                        </div>
                                    </v-col>
                                </v-row>
                                <div class="mt-3 pt-3 border-t-sm">
                                    <div class="text-caption">Display Message</div>
                                    <v-textarea :disabled="detailSaving" class="mt-3" rows="6" label="Notes" v-model="editNotes"></v-textarea>
                                </div>
                                <v-row class="mt-3 pt-3 border-t-sm">
                                    <v-col cols="2">
                                        <div class="text-caption d-flex">
                                            Profability
                                            <v-tooltip interactive>
                                                <template v-slot:activator="{ props: activatorProps }">
                                                    <v-icon icon="mdi-information-outline" v-bind="activatorProps"></v-icon>
                                                </template>
                                                <div>
                                                    <table class="d-table help-table">
                                                        <thead>
                                                            <tr>
                                                                <th class="text-left w-20">Score</th>
                                                                <th class="text-left w-80">Meaning</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td class="score-col score-1"><strong>5</strong></td>
                                                                <td>Strong margins, fair pricing, minimum charges accepted</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-2"><strong>4</strong></td>
                                                                <td>Good margin, occasional price pressure</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-3"><strong>3</strong></td>
                                                                <td>OK margin, price sensitive</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-4"><strong>2</strong></td>
                                                                <td>Low margin, constant pressure</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-5"><strong>1</strong></td>
                                                                <td>Margin erosion / not worth it</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </v-tooltip>
                                        </div>
                                        <h2 :class="getClass(editProfability, true)">{{ editProfability }}</h2>
                                    </v-col>
                                    <v-col cols="3">
                                        <v-slider show-ticks="always"
                                                  v-model="editProfability"
                                                  :disabled="detailSaving"
                                                  :max="5"
                                                  :min="0"
                                                  :step="1"
                                                  tick-size="2"></v-slider>
                                    </v-col>
                                    <v-col cols="2">
                                        <div class="text-caption d-flex">
                                            Effort/Pain
                                            <v-tooltip interactive>
                                                <template v-slot:activator="{ props: activatorProps }">
                                                    <v-icon icon="mdi-information-outline" v-bind="activatorProps"></v-icon>
                                                </template>
                                                <div>
                                                    <table class="d-table help-table">
                                                        <thead>
                                                            <tr>
                                                                <th class="text-left w-20">Score</th>
                                                                <th class="text-left w-80">Meaning</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td class="score-col score-1"><strong>1</strong></td>
                                                                <td>Easy, predictable, low admin</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-2"><strong>2</strong></td>
                                                                <td>Normal effort</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-3"><strong>3</strong></td>
                                                                <td>Admin heavy, inconsistent</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-4"><strong>4</strong></td>
                                                                <td>Demanding, slow payers, lots of quoting</td>
                                                            </tr>
                                                            <tr>
                                                                <td class="score-col score-5"><strong>5</strong></td>
                                                                <td>Actively painful (chasing, arguing, chaos)</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </v-tooltip>
                                        </div>
                                        <h2 :class="getClass(editEffort, false)">{{ editEffort }}</h2>
                                    </v-col>
                                    <v-col cols="3">
                                        <v-slider show-ticks="always"
                                                  v-model="editEffort"
                                                  :max="5"
                                                  :min="0"
                                                  :step="1"
                                                  :disabled="detailSaving"
                                                  tick-size="2"></v-slider>
                                    </v-col>
                                    <v-col cols="2" class="text-right">
                                        <div class="text-caption font-weight-bold">
                                            Score
                                        </div>
                                        <h2 :class="getClass(editProfability-editEffort, false)">{{ editProfability-editEffort }}</h2>
                                    </v-col>
                                </v-row>
                                <div class="d-flex justify-end ga-1">
                                    <v-btn :loading="detailSaving"
                                           color="success"
                                           prepend-icon="mdi-content-save"
                                           variant="tonal"
                                           @click="saveDetail"
                                           dark>
                                        SAVE
                                    </v-btn>
                                    <v-btn dark @click="showDetails = false">
                                        CANCEL
                                    </v-btn>
                                </div>
                            </v-col>
                            <v-col cols="4" class="note-section">
                                <note-bar></note-bar>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-text>
                        <div class="history-footer pa-3">
                            <h5 class="pb-2 text-center border-b-sm mb-2">HISTORY</h5>
                            <div v-for="item in selectedDetails.budgets" class="hist-panel text-caption mb-1" :key="item.id">
                                <span class="text-muted"><span v-if="!item.budget && !item.notes && !item.profabilityRating && !item.effortRating">Initial details</span><span v-if="item.budget" class="mr-2"><b>Budget:</b> {{ numberWithCommas(item.budget, 0) }}</span><span v-if="item.notes" class="mr-2"><b>Notes:</b> {{ item.notes }}</span><span v-if="item.profabilityRating" class="mr-2"><b>Profability Rating:</b> {{ item.profabilityRating }}</span><span v-if="item.effortRating" class="mr-2"><b>Effort Rating:</b> {{ item.effortRating }}</span></span>
                                <span class="text-muted"> by <span class="font-weight-bold"><v-icon icon="mdi-account"></v-icon> {{ item.createdBy }}</span> at <span class="font-weight-bold"><v-icon icon="mdi-clock-outline"></v-icon> {{ $dayjs(item.dateUpdated).format("DD/MM/YYYY") }}</span>.</span>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </div>
    </div>
</template>
<style scoped>
    .score-1 {
        color: #27ae60;
    }

    .score-2 {
        color: #2980b9;
    }

    .score-3 {
        color: #f39c12;
    }

    .score-4 {
        color: #e74c3c;
    }

    .score-5 {
        color: #c0392b;
    }
    .help-table tr td, .help-table tr th {
        font-size:10px;
    }
    .help-text {
        line-height: 0.8rem;
        padding: 0.5rem;
    }
    .history-footer {
        background-color: rgb(var(--v-theme-surface));
        margin-top: 20px;
    }
    .note-section {
        padding: 0;
        border-left: 1px solid #dee2e6 !important;
        border-right: 1px solid #dee2e6 !important;
        height: 700px;
        overflow-y:auto;
    }
    .perc-chip {
        padding: 2px 5px!important;
        font-size: 12px!important;
    }
    .detail-table{
        font-size:13px;
    }
</style>
<script>
    import { numberWithCommas } from "../../mixins/numberMixins";
    import { mapActions, mapState } from "vuex";
    function initialState() {
        return {
            loading: false,
            saving: false,
            data: null,
            loaded: false,
            showDetails: false,
            selectedDetails: null,
            editBudget: null,
            editProfability: 0,
            editEffort: 0, 
            editNotes: null,
            editGroup: null,
            detailSaving: false,
            monthsArray: []
        };
    }
    export default {
        name: "BudgetReport",
        data: function () {
            return initialState();
        },
        mounted: function () {
            let processMonth = this.$dayjs().month() + 1;
            while (processMonth >= 1 && this.monthsArray.length < 3) {
                this.monthsArray.push({ index: processMonth, name: this.$dayjs().month(processMonth - 1).format('MMM') });
                processMonth--;
            }
            this.monthsArray = this.monthsArray.reverse();
            this.loadContactGroups();
            this.loadReport();
        },
        props: {
            definition: Object
        },
        components: {
        },
        methods: {
            ...mapActions([
                "initNotes",
                "loadContactGroups",
            ]),
            scrollToGroup(groupName) {
                const ref = this.$refs[groupName];
                if (ref) {
                    const element = Array.isArray(ref) ? ref[0] : ref;
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            },
            loadReport(){
                this.errors = null;
                // All good - Run
                this.loading = true;
                let query = [];
                this.$api.get(this.definition.route)
                    .then(res => {
                        this.loading = false;
                        this.data = res.data;
                        this.buildGraphData();
                    }).catch(error => {
                        this.loading = false;
                    });
            },
            getClass(value, reverse) {
                return "score-col score-" + (reverse ? 6 - value : value );
            },
            openDetails(item) {
                this.selectedDetails = item;
                this.editBudget = item.activeBudget.budget || null;
                this.editProfability = item.activeBudget.profabilityRating || 0;
                this.editEffort = item.activeBudget.effortRating || 0;
                this.editNotes = item.activeBudget.notes || null;
                this.editGroup = item.contact.contactGroup;
                this.showDetails = true;
                let filters = [this.$constants.notes.TYP_ENTITY, this.$constants.notes.TYP_BUDGET];
                this.initNotes({ classId: item.contact.id, className: 'Contact', createType: this.$constants.notes.TYP_BUDGET, defaultFilters: [this.$constants.notes.TYP_BUDGET], selectionFilters: filters });

            },
            saveDetail() {
                this.detailSaving = true;
                const budget = {
                    year: this.data.year,
                    budget: this.editBudget ? parseFloat(this.editBudget) : null,
                    profabilityRating: this.editProfability,
                    effortRating: this.editEffort,
                    notes: this.editNotes,
                    contactGroupId: this.editGroup.id
                };
                this.$api.post(`contacts/${this.selectedDetails.contact.id}/budgets`, budget)
                    .then(res => {
                        this.data = res.data;
                        this.showDetails = false;
                        this.detailSaving = false;
                    })
                    .catch(() => {
                        this.detailSaving = false;
                    });
            },
            numberWithCommas,
            groupTotal(group, field) {
                return group.contacts.reduce((sum, c) => sum + (c.activeBudget[field] || 0), 0);
            },
            monthTotal(group, index) {
                return group.contacts.reduce((sum, c) => sum + (c.activeBudget.monthlyTotals[index-1] || 0), 0);
            },
            allGroupTotal(field) {
                if (!this.data) return 0;
                return this.data.contactGroups.reduce((sum, group) => sum + this.groupTotal(group, field), 0);
            },
            lastYearTotal(budget) {
                if (budget.lastMonthlyTotals) {
                    return budget.lastMonthlyTotals.reduce((acc, num) => acc + num, 0);
                }
                return 0;
            },
            lastYearGroupTotal(group) {
                return group.contacts.reduce((sum, c) => sum + (this.lastYearTotal(c.activeBudget) || 0), 0);
            },
            groupPercentDiff(group) {
                const estimate = this.groupTotal(group, 'estimatedActual');
                const budget = this.groupTotal(group, 'budget');
                if (!budget) return 0;
                return ((estimate - budget) / budget) * 100;
            },
            groupDetails(groupName) {
                switch (groupName) {
                    case "Managed":
                        return { name: groupName, colour: "indigo-lighten-1", icon: 'mdi-strategy' };
                    case "Standard":
                        return { name: groupName, colour: "blue-grey-lighten-1", icon: 'mdi-clipboard-account' };
                    case "Transactional":
                        return { name: groupName, colour: "brown-lighten-1", icon: 'mdi-invoice-fast-outline' };
                }
            },
            ratingClass(profability, effort) {
                var rating = profability - effort;
                if (rating < -2) {
                    return "text-red-darken-4"
                } else if (rating < 0) {
                    return "text-red-darken-1"
                } else if (rating > 2) {
                    return "text-green-darken-1"
                } else if (rating > 0) {
                    return "text-green-darken-4"
                }
            },
            diffClass(percentage, actual) {
                if (!percentage) {
                    if (actual && actual > 0) {
                        return 'bg-green-lighten-4';
                    }
                    return null;
                }
                if (percentage < 0) {
                    return 'bg-red-lighten-3';
                } else if (percentage > 40) {
                    return 'bg-green-lighten-3';
                }
            }
        },
        computed: {
            ...mapState({
                contactGroups: (state) => state.contactGroups.contactGroups,
                contactGroupsLoading: (state) => state.contactGroups.contactGroupsLoading
            }),
            allGroupBudget() {
                if (!this.data) return 0;
                return this.allGroupTotal('budget');
            },
            allGroupEstimate() {
                if (!this.data) return 0;
                return this.allGroupTotal('estimatedActual');
            },
            allGroupDiff() {
                if (!this.allGroupBudget) return 0;
                return ((this.allGroupEstimate - this.allGroupBudget) / this.allGroupBudget) * 100;
            }
        }
    };
</script>